import { ActionCardModel, checkVisibility } from './actionCard'
import {
  ActionCardData,
  SectionData,
  RecommendationFragmentType,
  AssessmentData,
  Element,
  ResponseData,
  WidgetData,
  GenericAiWidgetData,
  Option,
  UpdateWithAiWidgetData,
} from './types'
import { ElementKind, sectionTypes, WidgetType } from './enums'

type TagControllerItem = (SectionData | Element) & {
  kind?: ElementKind
  responses?: ResponseData[]
  type?: sectionTypes
  setTags?: Option[] | null
}

type TagsMap = Map<number | string, number[]>

/**
 * Initializes the `conditionalItems` property on the Action Card —
 * the list of all currently active tag IDs.
 * @param ac Action Card data
 */
function assignConditionalItems(model: ActionCardModel): void {
  const tagsMap: TagsMap = new Map()

  // IDs of elements whose tags must be suppressed because their parent
  // section is hidden.
  const hidingElementIds: number[] = []

  model.nodes.forEach(n => {
    const data = n.data as TagControllerItem

    if (data.type === sectionTypes.CONTENT) {
      processContentSection(data, model.data, tagsMap, hidingElementIds)
    }

    if (data.kind === ElementKind.ASSESSMENT) {
      processAssessmentElement(data, model.data, tagsMap, hidingElementIds)
    }

    if (data.kind === ElementKind.RECOMMENDATION) {
      processRecommendationElement(data, model.data, tagsMap, hidingElementIds)
    }

    if (data.kind === ElementKind.WIDGET) {
      processWidgetElement(data, model.data, tagsMap, hidingElementIds)
    }

    model.data.conditionalItems = Array.from(tagsMap.values()).flat()
  })
}

// Section handlers
function hideAssessmentInSection(element: AssessmentData, tagsMap: TagsMap, hidingElementIds: number[]): void {
  hidingElementIds.push(element.id!)
  element.checked = element.isAutoCollapse ? false : element.checked
  element.responses?.forEach(resp => {
    resp.tags?.forEach(() => tagsMap.delete(resp.id))
    resp.checked = false
  })
}

function hideRecommendationInSection(
  element: Element & { setTags?: Option[] | null },
  tagsMap: TagsMap,
  hidingElementIds: number[],
): void {
  hidingElementIds.push(element.id!)
  element.setTags?.forEach(() => tagsMap.delete(element.id!))
}

function processContentSection(
  data: TagControllerItem,
  ac: ActionCardData,
  tagsMap: TagsMap,
  hidingElementIds: number[],
): void {
  const isVisible = checkVisibility(data, ac)

  if (isVisible) {
    tagsMap.set(data.id!, data.setTags?.map(t => t.id) || [])
    return
  }

  data.setTags?.forEach(() => tagsMap.delete(data.id!))
  ;(data as SectionData).elements?.forEach(element => {
    if (element.kind === ElementKind.ASSESSMENT) {
      hideAssessmentInSection(element as AssessmentData, tagsMap, hidingElementIds)
    }

    if (element.kind === ElementKind.RECOMMENDATION) {
      hideRecommendationInSection(element, tagsMap, hidingElementIds)
    }
  })
}

// Element handlers
function processAssessmentElement(
  data: TagControllerItem,
  ac: ActionCardData,
  tagsMap: TagsMap,
  hidingElementIds: number[],
): void {
  const element = data as AssessmentData
  const isActiveAndVisible = checkVisibility(data, ac) && !hidingElementIds.includes(element.id!)

  if (isActiveAndVisible) {
    data.responses?.forEach(resp => {
      if (resp.checked) {
        tagsMap.set(resp.id!, resp.tags?.map(t => t.id) || [])
      } else {
        tagsMap.delete(resp.id)
      }
    })
    return
  }

  element.checked = element.isAutoCollapse ? false : element.checked
  element.responses?.forEach(resp => {
    resp.tags?.forEach(() => tagsMap.delete(resp.id))
    resp.checked = false
  })
}

function processRecommendationElement(
  data: TagControllerItem,
  ac: ActionCardData,
  tagsMap: TagsMap,
  hidingElementIds: number[],
): void {
  const element = data as RecommendationFragmentType
  const isActiveAndVisible = checkVisibility(data, ac) && !hidingElementIds.includes(element.id!)

  if (isActiveAndVisible) {
    tagsMap.set(element.id!, element.setTags?.map(t => t.id) || [])

    element.widgets?.forEach(w => setWidgetTags(w, tagsMap))
    return
  }

  element.setTags?.forEach(() => tagsMap.delete(element.id!))
  element.widgets?.forEach(w => deleteWidgetsTags(w, tagsMap))
}
function processWidgetElement(
  data: TagControllerItem,
  ac: ActionCardData,
  tagsMap: TagsMap,
  hidingElementIds: number[],
): void {
  const element = data as unknown as WidgetData
  const isActiveAndVisible = checkVisibility(data, ac) && !hidingElementIds.includes(element.id!)

  if (isActiveAndVisible) {
    tagsMap.set(element.id!, element.setTags?.map(t => t.id) || [])
    setWidgetTags(element, tagsMap)
    return
  }

  element.setTags?.forEach(() => tagsMap.delete(element.id!))
  deleteWidgetsTags(element, tagsMap)
}

function setWidgetTags(element: WidgetData, tagsMap: TagsMap) {
  // Should be reworked in the future to use the state.isActive for tags management
  //
  // Special case for Generic AI Widget: its tags should be applied only
  // when it has a value, otherwise they should be removed.
  // This logic is strange because the tags applying should be managed with the state.isActive prop
  if (isGenericAiPromptWidget(element)) {
    manageWidgetTags(element, tagsMap, !!element.value)

    return
  }

  if (isUpdateWithAiWidget(element)) {
    manageWidgetTags(element, tagsMap, element.isAcUpdated)

    return
  }

  tagsMap.set(element.id!, element.setTags?.map(t => t.id) || [])
  element.states?.forEach(state => {
    if (state.isActive) {
      tagsMap.set(JSON.stringify(state), state.activeTags?.map(t => t.id) || [])
    } else {
      tagsMap.delete(JSON.stringify(state))
    }
  })
}

function manageWidgetTags(element: WidgetData, tagsMap: TagsMap, applyTags: boolean) {
  if (applyTags) {
    tagsMap.set(element.id, element.setTags?.map(t => t.id) || [])
  } else {
    tagsMap.delete(element.id)
  }
}

function isGenericAiPromptWidget(element: WidgetData): element is GenericAiWidgetData {
  return element.type === WidgetType.GenericAiPrompt
}

function isUpdateWithAiWidget(element: WidgetData): element is UpdateWithAiWidgetData {
  return element.type === WidgetType.UpdateWithAi
}

function deleteWidgetsTags(element: WidgetData, tagsMap: TagsMap) {
  element.setTags?.forEach(() => tagsMap.delete(element.id!))
  element.states?.forEach(state => {
    state.activeTags?.forEach(() => tagsMap.delete(JSON.stringify(state)))
    state.isActive = false
  })
}

export default assignConditionalItems
